import {EXPENSE_LINES, ECONOMIC_VACANCY_LINES, PRICING_RANGE_STEPS} from './fields';

/**
 * The formula engine of MF_BOV_Underwriting_Template.xlsx.
 *
 * calculate(inputs) takes the stored inputs (fields.js) and returns every figure the workbook
 * derives, sheet by sheet. It is pure - no React, no I/O - so the popup, the save and the BOV
 * print all read the same numbers. Cell references in the comments are the workbook's.
 *
 * Conventions:
 *   - Percent inputs are whole numbers (6.6 = 6.6%) and are returned as decimals (0.066).
 *   - Every division is Excel's IFERROR(a/b, 0): a zero or blank divisor gives 0.
 *   - Deductions (vacancy lines) are returned negative, as the Pricing sheet shows them.
 */

export function toNumber(value) {
    if (value === null || value === undefined || value === '') return 0;
    const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/[,$%\s]/g, ''));
    return isFinite(n) ? n : 0;
}

function rate(value) {
    return toNumber(value) / 100;
}

function div(a, b) {
    const result = b ? a / b : 0;
    return isFinite(result) ? result : 0;
}

function sum(list, pick) {
    return list.reduce((total, item) => total + (pick ? pick(item) : item), 0);
}

/** Excel PMT(rate, nper, -pv): the level payment that repays `principal`. */
export function pmt(monthlyRate, periods, principal) {
    if (!periods) return 0;
    if (!monthlyRate) return principal / periods;
    return principal * monthlyRate / (1 - Math.pow(1 + monthlyRate, -periods));
}

/** Current Rent Roll sheet (rows 5..n, totals row 8, occupancy B11). */
function currentRentRoll(unitMix) {
    const rows = unitMix.map(row => {
        const units = toNumber(row.units);
        const avgSf = toNumber(row.avg_sf);
        const rent = toNumber(row.rent);
        const proformaRent = toNumber(row.proforma_rent);
        const monthlyRent = units * rent;                                   // J
        return {
            floor_plan: row.floor_plan,
            units,                                                          // B
            avg_sf: avgSf,                                                  // C
            rent,                                                           // D
            rent_sf: div(rent, avgSf),                                      // E
            proforma_rent: proformaRent,                                    // F
            proforma_rent_sf: div(proformaRent, avgSf),                     // G
            occupied_units: toNumber(row.occupied_units),                   // H
            total_sf: units * avgSf,                                        // I
            monthly_rent: monthlyRent,
            monthly_proforma_rent: units * proformaRent,                    // K
            annual_rent: monthlyRent * 12,                                  // L
        };
    });

    const units = sum(rows, r => r.units);                                  // B8 - the only unit count used anywhere
    const totalSf = sum(rows, r => r.total_sf);                             // I8
    const monthlyRent = sum(rows, r => r.monthly_rent);                     // J8
    const monthlyProformaRent = sum(rows, r => r.monthly_proforma_rent);    // K8
    const avgSf = div(totalSf, units);                                      // C8
    const rent = div(monthlyRent, units);                                   // D8
    const proformaRent = div(monthlyProformaRent, units);                   // F8
    const occupiedUnits = sum(rows, r => r.occupied_units);                 // H8

    return {
        rows,
        total: {
            floor_plan: 'Total/ Weighted Avg.',
            units,
            avg_sf: avgSf,
            rent,
            rent_sf: div(rent, avgSf),                                      // E8
            proforma_rent: proformaRent,
            proforma_rent_sf: div(proformaRent, avgSf),                     // G8
            occupied_units: occupiedUnits,
            total_sf: totalSf,
            monthly_rent: monthlyRent,
            monthly_proforma_rent: monthlyProformaRent,
            annual_rent: sum(rows, r => r.annual_rent),                     // L8
        },
        occupancy: div(occupiedUnits, units),                               // B11
    };
}

/** Proforma Rent Roll sheet - a read-only view of the current roll's proforma column. */
function proformaRentRoll(rentRoll) {
    const rows = rentRoll.rows.map(row => {
        const monthly = row.units * row.proforma_rent;                      // D
        return {
            floor_plan: row.floor_plan,
            units: row.units,
            proforma_rent: row.proforma_rent,
            monthly_proforma_rent: monthly,
            annual_proforma_rent: monthly * 12,                             // E
        };
    });
    const units = sum(rows, r => r.units);
    const monthly = sum(rows, r => r.monthly_proforma_rent);
    return {
        rows,
        total: {
            floor_plan: 'Total',
            units,
            proforma_rent: div(monthly, units),                             // C8
            monthly_proforma_rent: monthly,
            annual_proforma_rent: sum(rows, r => r.annual_proforma_rent),   // E8
        },
    };
}

/** Current / Proforma Reimbursements sheets. */
function otherIncome(list, units) {
    const rows = list.map(row => {
        const amount = toNumber(row.amount);
        return {category: row.category, amount, per_unit: div(amount, units)};
    });
    const total = sum(rows, r => r.amount);                                 // C12
    return {rows, total, total_per_unit: div(total, units)};
}

/** One income/expense line in both columns, with the per-unit figures Pricing shows. */
function line(current, proforma, units) {
    return {
        current,
        current_per_unit: div(current, units),
        proforma,
        proforma_per_unit: div(proforma, units),
    };
}

/** Valuation block (Pricing rows 36-46) for one column. */
function valuation(noi, capRate, inputs, units, rentableSf) {
    const price = div(noi, capRate);                                        // 38
    const downPayment = price * rate(inputs.down_payment_percent);          // 41
    const loanAmount = price - downPayment;                                 // 42
    const debtService = pmt(rate(inputs.interest_rate) / 12, toNumber(inputs.amortization) * 12, loanAmount) * 12; // 43
    const cashFlow = noi - debtService;                                     // 44
    return {
        noi,
        cap_rate: capRate,
        price,
        price_per_unit: div(price, units),                                  // 39
        psf: div(price, rentableSf),                                        // 40
        down_payment: downPayment,
        loan_amount: loanAmount,
        debt_service: debtService,
        cash_flow: cashFlow,
        cash_on_cash: div(cashFlow, downPayment),                           // 45
        dscr: div(noi, debtService),                                        // 46
    };
}

export default function calculate(inputs) {
    const rentRoll = currentRentRoll(inputs.unit_mix || []);
    const units = rentRoll.total.units;
    const rentableSf = rentRoll.total.total_sf;

    // ---- Property Information
    const millRate = rate(inputs.mill_rate);
    const propertyInfo = {
        total_units: units,                                                 // C13
        rentable_sf: rentableSf,                                            // C14
        parking_per_unit: div(toNumber(inputs.parking_spaces), units),      // C15
        current_tax_liability: toNumber(inputs.current_tax_appraised_value) * millRate, // C18
        new_taxes: toNumber(inputs.new_assessed_value) * millRate,          // C20
    };

    const proformaRoll = proformaRentRoll(rentRoll);
    const currentOtherIncome = otherIncome(inputs.current_other_income || [], units);
    const proformaOtherIncome = otherIncome(inputs.proforma_other_income || [], units);

    // ---- Pricing: income (rows 15-21)
    const gpr = {current: rentRoll.total.annual_rent, proforma: proformaRoll.total.annual_proforma_rent};
    // Physical Vacancy (Expenses C20/D20). The `vacancy` input name predates the v2 rename.
    const vacancy = {current: toNumber(inputs.vacancy), proforma: toNumber(inputs.proforma_vacancy)};
    const economic = ECONOMIC_VACANCY_LINES.map(item => ({
        key: item.key,
        label: item.label,
        current: toNumber(inputs[item.key]),
        proforma: toNumber(inputs[`proforma_${item.key}`]),
    }));
    const economicTotal = {current: sum(economic, e => e.current), proforma: sum(economic, e => e.proforma)}; // Expenses C25/D25

    const otherIncomeTotal = {current: currentOtherIncome.total, proforma: proformaOtherIncome.total};
    const totalGross = {
        current: gpr.current - vacancy.current - economicTotal.current + otherIncomeTotal.current,           // C19
        proforma: gpr.proforma - vacancy.proforma - economicTotal.proforma + otherIncomeTotal.proforma,      // E19
    };
    // v2 dropped the Vacancy Factor deduction (old row 20), so EGI is Total Gross Revenue (C21 = C19).
    const egi = {current: totalGross.current, proforma: totalGross.proforma};

    // ---- Expenses sheet (rows 8-17), returned in Marketing order (see EXPENSE_LINES)
    const managementFeeRate = {current: rate(inputs.management_fee_percent), proforma: rate(inputs.proforma_management_fee_percent)};
    const expenseValue = (key, column) => {
        if (key === 'real_estate_taxes') {
            return column === 'current' ? propertyInfo.current_tax_liability : propertyInfo.new_taxes;      // C8/D8
        }
        if (key === 'management_fee') return managementFeeRate[column] * egi[column];                      // C13/D13 (of EGI)
        return toNumber(inputs[column === 'current' ? key : `proforma_${key}`]);
    };
    const expenseLines = EXPENSE_LINES.map(item => {
        const current = expenseValue(item.key, 'current');
        return {
            key: item.key,
            label: item.label,
            current,
            proforma: expenseValue(item.key, 'proforma'),
            current_per_unit: div(current, units),                          // E
            current_psf: div(current, rentableSf),                          // F
        };
    });
    const totalExpenses = {current: sum(expenseLines, e => e.current), proforma: sum(expenseLines, e => e.proforma)}; // 17

    const vacancyShare = (value, column) => div(value, gpr[column]);         // Expenses F/G: % of GPR
    const expenses = {
        egi,                                                                // Expenses C5/D5
        lines: expenseLines,
        total: {
            ...totalExpenses,
            current_per_unit: div(totalExpenses.current, units),
            current_psf: div(totalExpenses.current, rentableSf),
        },
        vacancy: {
            ...vacancy,
            current_per_unit: div(vacancy.current, units),
            current_share: vacancyShare(vacancy.current, 'current'),
            proforma_share: vacancyShare(vacancy.proforma, 'proforma'),
        },
        economic: economic.map(item => ({
            ...item,
            current_per_unit: div(item.current, units),
            current_share: vacancyShare(item.current, 'current'),
            proforma_share: vacancyShare(item.proforma, 'proforma'),
        })),
        economicTotal: {
            ...economicTotal,
            current_per_unit: div(economicTotal.current, units),
            current_share: vacancyShare(economicTotal.current, 'current'),
            proforma_share: vacancyShare(economicTotal.proforma, 'proforma'),
        },
    };

    // ---- Pricing: income & expenses table (rows 15-33)
    const noi = {current: egi.current - totalExpenses.current, proforma: egi.proforma - totalExpenses.proforma}; // 33
    const income = {
        gross_potential_rent: line(gpr.current, gpr.proforma, units),
        vacancy: line(-vacancy.current, -vacancy.proforma, units),
        economic_vacancy: line(-economicTotal.current, -economicTotal.proforma, units),
        other_income: line(otherIncomeTotal.current, otherIncomeTotal.proforma, units),
        total_gross_revenue: line(totalGross.current, totalGross.proforma, units),
        effective_gross_income: line(egi.current, egi.proforma, units),
        expenses: expenseLines.map(e => ({key: e.key, label: e.label, ...line(e.current, e.proforma, units)})),
        total_expenses: line(totalExpenses.current, totalExpenses.proforma, units),
        net_operating_income: line(noi.current, noi.proforma, units),
        // The percentages the row labels quote, per column.
        rates: {
            vacancy: {current: expenses.vacancy.current_share, proforma: expenses.vacancy.proforma_share},
            economic_vacancy: {current: expenses.economicTotal.current_share, proforma: expenses.economicTotal.proforma_share},
            management_fee: managementFeeRate,
        },
    };

    // ---- Pricing: valuation (rows 36-46). Both columns use the one cap rate (E37 = $C$5).
    const capRate = rate(inputs.cap_rate);
    const valuations = {
        current: valuation(noi.current, capRate, inputs, units, rentableSf),
        proforma: valuation(noi.proforma, capRate, inputs, units, rentableSf),
    };

    // ---- Pricing: pricing range (H4:M14) - current NOI at the cap rate +/- BPS steps.
    const bps = rate(inputs.bps_change);
    const pricingRange = PRICING_RANGE_STEPS.map(step => {
        const v = valuation(noi.current, capRate + step * bps, inputs, units, rentableSf);
        return {
            step,
            cap_rate: v.cap_rate,
            price: v.price,
            price_per_unit: v.price_per_unit,
            psf: v.psf,
            down_payment: v.down_payment,
            loan_amount: v.loan_amount,
            loan_payment: v.debt_service,
            cash_flow: v.cash_flow,
            cash_on_cash: v.cash_on_cash,
            dscr: v.dscr,
        };
    });

    return {
        units,
        rentableSf,
        propertyInfo,
        rentRoll,
        proformaRentRoll: proformaRoll,
        otherIncome: {current: currentOtherIncome, proforma: proformaOtherIncome},
        expenses,
        income,
        valuation: valuations,
        pricingRange,
    };
}
